import {ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Params, Router, UrlSerializer} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {isEmpty} from '@spartacus/cart/base/core';
import {ActiveCartFacade, CartAddEntryFailEvent, CartEvent, CartUpdateEntryFailEvent} from '@spartacus/cart/base/root';
import {EventService, GlobalMessageService, HttpErrorModel, RoutingService, StateEventService} from '@spartacus/core';
import {
  CommonConfigurator,
  ConfiguratorRouter,
  ConfiguratorRouterExtractorService,
} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorCartService,
  ConfiguratorExpertModeService,
  ConfiguratorFormComponent,
  ConfiguratorGroupsService,
} from '@spartacus/product-configurator/rulebased';
import {LaunchDialogService} from '@spartacus/storefront';
import {combineLatest, merge, Observable, of, Subject} from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  share,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import {ExternalAttribute} from '../../models';
import {
  CartService,
  ConfiguratorUriParameterService,
  DcpConfiguratorCommonsService,
  DCPConfiguratorDeepLinkingService,
  FeatureTogglesService,
} from '../../services';
import {fromProductConfigurator} from '../../store';
import {optionalServiceProviderTokens, serviceProviderTokens, valueProviderTokens} from '../../token/tokens';
import {deepEqual, SKIP_CONFIGURATOR, TRANSACTION_TYPE_ATTRIBUTE} from '../../utils';
import UiType = Configurator.UiType;
@UntilDestroy()
@Component({
  selector: 'dcp-configurator-single-page-form',
  templateUrl: './dcp-configurator-single-page-form.component.html',
  styleUrl: './dcp-configurator-single-page-form.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DcpConfiguratorSinglePageFormComponent extends ConfiguratorFormComponent implements OnInit, OnDestroy {
  private dcpActiveCartService = inject(serviceProviderTokens.ACTIVE_CART_SERVICE_PROVIDER);
  private unleashToggles = inject(valueProviderTokens.FEATURE_TOGGLES);
  private addToCartEventService = inject(optionalServiceProviderTokens.ADD_TO_CART_EVENT_SERVICE_PROVIDER, {
    optional: true,
  });
  private routerDataSub$ = new Subject<void>();

  stripeMessageDesign: {[key: string]: string} = {
    warning: 'Warning',
    error: 'Negative',
    info: 'Information',
  };

  @Input() hideConfiguration: boolean = false;

  bookMarkParams$: Observable<Params> = this.configDeepLinkingService.generateBookmarkParams();
  technicalGroupDisplay$ = this.featureToggleService
    .isEnabled(this.unleashToggles.cpsConfiguratorDisplayTechnicalGroup)
    .pipe(untilDestroyed(this));
  ownerType = CommonConfigurator.OwnerType;
  isSkipConfigurator: boolean = false;
  externalValidations?: any[];
  isCpsLoggingEnabled?: boolean;
  routerData: ConfiguratorRouter.Data = {} as ConfiguratorRouter.Data;

  override configuration$: Observable<Configurator.Configuration> = this.configRouterExtractorService
    .extractRouterData()
    .pipe(
      filter((routerData) => routerData.pageType === ConfiguratorRouter.PageType.CONFIGURATION),
      tap((routerData) => (this.routerData = routerData)),
      withLatestFrom(this.configuratorCommonsService.getState().configReseted),
      filter(([, isReseted]) => !isReseted),
      switchMap(() => this.handleReCreateCart()), // it is important to re-create cart, if needed, before resetting configuration
      switchMap(() => this.handleResetConfiguration()),
      switchMap(() =>
        this.configuratorCommonsService
          .getOrCreateConfiguration(this.routerData.owner)
          .pipe(distinctUntilChanged(deepEqual)),
      ),
    )
    .pipe(share());

  configurationWithMetaData$ = this.configDeepLinkingService.loadConfigurationWithMetaData(this.configuration$).pipe(
    tap((configuration) => {
      if (this.isCpsLoggingEnabled) {
        this.configDeepLinkingService.logIncorrectDeeplinkParameters(
          configuration,
          this.routerData?.externalAttributes,
        );
      }
    }),
  );

  hasPendingChanges$: Observable<boolean> = this.configRouterExtractorService.extractRouterData().pipe(
    switchMap((routerData) => this.configuratorCommonsService.hasPendingChanges(routerData.owner)),
    distinctUntilChanged(), // avoid subsequent emissions of the same value from the source observable
  );

  constructor(
    protected override configuratorCommonsService: DcpConfiguratorCommonsService,
    protected override configuratorGroupsService: ConfiguratorGroupsService,
    protected override configRouterExtractorService: ConfiguratorRouterExtractorService,
    protected configDeepLinkingService: DCPConfiguratorDeepLinkingService,
    protected featureToggleService: FeatureTogglesService,
    protected uriParamService: ConfiguratorUriParameterService,
    private eventService: EventService,
    private changeDetectorRef: ChangeDetectorRef,
    protected router: Router,
    protected override configExpertModeService: ConfiguratorExpertModeService,
    protected override launchDialogService: LaunchDialogService,
    protected configuratorCartService: ConfiguratorCartService,
    protected routingService: RoutingService,
    protected store: Store,
    protected override globalMessageService: GlobalMessageService,
    private serializer: UrlSerializer,
    private cartService: CartService,
    private activeCartFacade: ActiveCartFacade,
  ) {
    super(
      configuratorCommonsService,
      configuratorGroupsService,
      configRouterExtractorService,
      configExpertModeService,
      launchDialogService,
      globalMessageService,
    );
    this.featureToggleService
      .isEnabled(this.unleashToggles.cpsLoggingEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => (this.isCpsLoggingEnabled = enabled));

    this.configuratorCommonsService.setInitialLoadingState(true);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.configuratorCommonsService
      .getState()
      .updateFinished.pipe(
        distinctUntilChanged(deepEqual),
        switchMap((isUpdated: boolean) => {
          if (!isUpdated) {
            this.updateConfig().pipe(untilDestroyed(this)).subscribe();
          }
          return of(isUpdated);
        }),
        filter((updateFinished) => updateFinished),
        takeUntil(this.routerDataSub$),
      )
      .subscribe(() => {
        if (this.isSkipConfigurator) {
          this.skipConfigurator();
        }
      });
    this.handleAddToCartErrors();
  }

  handleAddToCartErrors(): void {
    merge(this.eventService.get(CartAddEntryFailEvent), this.eventService.get(CartUpdateEntryFailEvent))
      .pipe(untilDestroyed(this))
      .subscribe((event: CartAddEntryFailEvent | CartUpdateEntryFailEvent) => {
        if (!event) {
          return;
        }

        let description = 'configurator.error.contactUs.message';
        if (event instanceof CartAddEntryFailEvent) {
          const error = event?.error as HttpErrorModel;
          if ((error?.details?.length ?? 0) > 0 && error?.details?.[0].type === 'MultiCurrencyCartError') {
            description = 'configurator.error.addToCart.multiCurrencyCart';
          }
        }

        this.externalValidations = this.externalValidations || [];
        this.externalValidations.push({
          description: description,
          severity: 'error',
        });
        this.changeDetectorRef.detectChanges();
      });
  }

  checkTransactionType(type: string): Observable<boolean> {
    let transactionType = this.routerData?.externalAttributes?.find(
      (externalAttribute: ExternalAttribute) => externalAttribute.name === TRANSACTION_TYPE_ATTRIBUTE,
    )?.value;
    return of(transactionType === type);
  }

  handleReCreateCart(): Observable<any> {
    this.isSkipConfigurator = !!this.routerData?.externalAttributes?.find(
      (externalAttribute: ExternalAttribute) => externalAttribute.name === SKIP_CONFIGURATOR,
    )?.value;
    this.configuratorCommonsService.skipConfigurator.next(this.isSkipConfigurator);
    if (this.isSkipConfigurator) {
      return this.featureToggleService.isEnabled(this.unleashToggles.configuratorClearCartOnSkip).pipe(
        untilDestroyed(this),
        withLatestFrom(this.checkTransactionType('SUPPLIER_ORDER_NEW')),
        switchMap(([enabled, isSuppierOrderNewType]) => {
          if (enabled && isSuppierOrderNewType) {
            // new implementation with deleting and creating cart from the UI
            return this.reCreateCart();
          } else {
            // existing implementation with deleting and creating cart from the backend
            return of({});
          }
        }),
      );
    } else {
      return of({});
    }
  }

  reCreateCart(): Observable<boolean> {
    return this.activeCartFacade.requireLoadedCart().pipe(
      untilDestroyed(this),
      take(1),
      switchMap((cart) => {
        if (!isEmpty(cart)) {
          return this.cartService.deleteActiveCart().pipe(
            switchMap((isDeleted: boolean) => {
              if (isDeleted) {
                return this.cartService.createCart();
              } else {
                throw Error('Error while deleting cart');
              }
            }),
          );
        } else {
          return of(true);
        }
      }),
      map((isCartCreated: boolean) => {
        if (!isCartCreated) {
          throw Error('Error while creating cart');
        } else {
          return true;
        }
      }),
    );
  }

  skipConfigurator() {
    this.removeParamFromUrl(SKIP_CONFIGURATOR);
    combineLatest([
      this.store.select(fromProductConfigurator.selectFirstProductConfiguration).pipe(distinctUntilChanged(deepEqual)),
      this.configurationWithMetaData$,
      this.dcpActiveCartService.requireLoadedCart(false),
      this.hasPendingChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(
          ([state, configuration, _, hasPendingChanges]) =>
            state.success &&
            state.processesCount === 0 &&
            configuration.consistent &&
            !configuration.priceSummary?.pricingError &&
            configuration.totalNumberOfIssues === 0 &&
            configuration.validation?.length === 0 &&
            !hasPendingChanges,
        ),
        // as we need to filter for successful checks, we need a timeout to stop the flow if errors persist
        timeout(45000),
        take(1),
      )
      .subscribe({
        next: ([_, configuration]) => {
          this.routerDataSub$.next();
          this.routerDataSub$.complete();
          this.directCheckout(configuration);
        },
        error: (err) => {
          // deal with timeout
          this.isSkipConfigurator = false;
          this.configuratorCommonsService.skipConfigurator.next(false);
          console.error('An error occurred, unable to skip configurator');
          console.error(err);
        },
      });
  }

  directCheckout(configuration: Configurator.Configuration): void {
    if (!this.routerData) return;
    this.configuratorCartService.addToCart(this.routerData.owner.id, configuration.configId, this.routerData.owner);
    if (this.addToCartEventService) {
      this.dcpActiveCartService
        .getActiveCart()
        .pipe(untilDestroyed(this), skip(1), first())
        .subscribe((cart: any) => {
          this.addToCartEventService?.triggerAddToCartEvent(cart, configuration);
        });
      this.addToCartEventService.triggerEddlAddToCartEvent(configuration);
    }
    this.routingService.go({cxRoute: 'checkoutDataEntry'});
  }

  anyValueSelected = (attribute: Configurator.Attribute) => !!attribute.values?.filter((val) => val.selected).length;

  handleResetConfiguration(): Observable<boolean> {
    this.deleteConfigOnResetParam(this.routerData);
    return this.configuratorCommonsService.getState().configReseted;
  }

  deleteConfigOnResetParam(routingData: any) {
    const resetParam = this.uriParamService.getResetParam() === 'true';
    if (resetParam && routingData?.owner?.type !== this.ownerType.CART_ENTRY) {
      this.configuratorCommonsService.setConfigResetedState(true);
      this.removeParamFromUrl('reset');
      this.deleteConfiguration();
    }
  }

  updateConfig(): Observable<any> {
    return this.configDeepLinkingService.getExternalAttributes().pipe(
      untilDestroyed(this),
      delay(100),
      distinctUntilChanged(deepEqual),
      map((newAttributes: any) => {
        if (!!newAttributes && newAttributes.length !== 0) {
          this.configuratorCommonsService.updateMultipleAttributesOfConfiguration(
            this.routerData.owner.key,
            newAttributes,
          );
        } else {
          this.configuratorCommonsService.setUpdateFinishedState(true);
        }
      }),
    );
  }

  deleteConfiguration(): void {
    this.configuratorCommonsService.setInitialLoadingState(true);
    this.configRouterExtractorService
      .extractRouterData()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((routingData) => this.configuratorCommonsService.removeBackendConfiguration(routingData.owner));
  }

  removeParamFromUrl(param: string): void {
    this.router.navigate([], {
      queryParams: {
        [param]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  isGroupEmpty = (group: Configurator.Group) =>
    group.attributes?.filter((attr) => !(attr.hidden || this.isAttributeReadonlyAndNotFilled(attr))).length === 0;

  isAttributeReadonlyAndNotFilled = (attr: Configurator.Attribute) =>
    attr.uiType === UiType.READ_ONLY && !(attr.selectedSingleValue || this.anyValueSelected(attr));

  getGroups(technicalGroupDisplay: boolean, configuration: Configurator.Configuration) {
    return technicalGroupDisplay || configuration.ngcs ? configuration.flatGroups : configuration.groups;
  }

  isConflictGroupType(groupType: Configurator.GroupType): boolean {
    return this.configuratorGroupsService.isConflictGroupType(groupType);
  }

  bookmark() {
    let queryParams: Params;
    this.configDeepLinkingService
      .generateBookmarkParams()
      .pipe(untilDestroyed(this))
      .subscribe((params) => {
        queryParams = params;
        const fullRoute = this.serializer.serialize(this.router.createUrlTree([], {queryParams}));
        this.router.navigateByUrl(fullRoute);
      })
      .unsubscribe();
  }

  getFormattedSeverity(severity: string): string | undefined {
    const formattedSeverity = this.stripeMessageDesign[severity.toLowerCase()];
    return formattedSeverity ? formattedSeverity : undefined;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.configuratorCommonsService.setConfigResetedState(false);
    this.configuratorCommonsService.setUpdateFinishedState(false);
  }
}
